import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  id: string;
  name: string;
  email: string;
  organization: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isHomepageUnlocked: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (name: string, email: string, password: string, organization: string) => Promise<boolean>;
  logout: () => void;
  unlockHomepage: (password: string) => boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isHomepageUnlocked, setIsHomepageUnlocked] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('clear_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('clear_user');
      }
    }

    // Check for homepage unlock in sessionStorage (clears when browser closes)
    const homepageUnlocked = sessionStorage.getItem('clear_homepage_unlocked');
    if (homepageUnlocked === 'true') {
      setIsHomepageUnlocked(true);
    }

    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    // Simulate API call - in production this would validate against backend
    // For now, check if user exists in localStorage (from signup)
    const storedUsers = localStorage.getItem('clear_users');

    if (storedUsers) {
      try {
        const users = JSON.parse(storedUsers);
        const foundUser = users.find(
          (u: any) => u.email === email && u.password === password
        );

        if (foundUser) {
          const { password: _, ...userWithoutPassword } = foundUser;
          setUser(userWithoutPassword);
          localStorage.setItem('clear_user', JSON.stringify(userWithoutPassword));
          return true;
        }
      } catch (e) {
        console.error('Error parsing stored users:', e);
      }
    }

    return false;
  };

  const signup = async (
    name: string,
    email: string,
    password: string,
    organization: string
  ): Promise<boolean> => {
    // Simulate API call - in production this would create user in backend
    try {
      const newUser = {
        id: `user-${Date.now()}`,
        name,
        email,
        organization,
        password // In production, this would be hashed
      };

      // Get existing users or create new array
      const storedUsers = localStorage.getItem('clear_users');
      const users = storedUsers ? JSON.parse(storedUsers) : [];

      // Check if email already exists
      if (users.some((u: any) => u.email === email)) {
        return false;
      }

      // Add new user
      users.push(newUser);
      localStorage.setItem('clear_users', JSON.stringify(users));

      // Log in the user
      const { password: _, ...userWithoutPassword } = newUser;
      setUser(userWithoutPassword);
      localStorage.setItem('clear_user', JSON.stringify(userWithoutPassword));

      return true;
    } catch (e) {
      console.error('Signup error:', e);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('clear_user');
  };

  const unlockHomepage = (password: string): boolean => {
    if (password === 'CLEAR') {
      setIsHomepageUnlocked(true);
      sessionStorage.setItem('clear_homepage_unlocked', 'true');
      return true;
    }
    return false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isHomepageUnlocked,
        login,
        signup,
        logout,
        unlockHomepage,
        isLoading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
